<template>
  <div class="space-y-2">
    <div v-if="isLoadingContacts" class="flex justify-center py-8">
      <i class="pi pi-spin pi-spinner text-2xl text-gray-400" />
    </div>

    <template v-else>
      <!-- Parents/payers of this player: card rows per the design -->
      <div
        v-for="contact in contacts"
        :key="`contact-${contact.id}`"
        class="flex items-center justify-between rounded-lg bg-[var(--p-surface-50)] px-4 py-3"
        data-testid="contact-relationship-row"
      >
        <div class="flex items-center gap-3">
          <UserAvatar
            :initials="getUserInitials({ name: contact.name } as any)"
          />
          <div>
            <div class="text-sm font-medium">
              {{ contact.name || "N/A" }}
            </div>
            <div class="text-xs text-gray-500">
              {{ contact.email || "No email" }}
            </div>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Tag
            v-if="contact.is_parent !== false"
            v-bind="getRoleTagProps('Parent')"
            value="Parent"
          />
          <!-- Default Payer replaces Payer, per design -->
          <Tag
            v-if="contact.primary_contact"
            severity="secondary"
            value="Default Payer"
          />
          <Button
            icon="pi pi-ellipsis-h"
            text
            rounded
            size="small"
            data-testid="contact-row-menu"
            :aria-label="`Actions for ${contact.name}`"
            @click="openContactMenu($event, contact)"
          />
        </div>
      </div>

      <!-- Players this person manages: badges from the parent's perspective -->
      <div
        v-for="player in players"
        :key="`player-${player.id}`"
        class="flex items-center justify-between rounded-lg bg-[var(--p-surface-50)] px-4 py-3"
        data-testid="player-relationship-row"
      >
        <div class="flex items-center gap-3">
          <UserAvatar
            :initials="getUserInitials({ name: player.name } as any)"
          />
          <div>
            <div class="text-sm font-medium">
              {{ player.name || "N/A" }}
            </div>
            <div class="text-xs text-gray-500">
              {{ player.email || player.team_name || "No email" }}
            </div>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Tag severity="info" value="Player" />
          <Button
            icon="pi pi-ellipsis-h"
            text
            rounded
            size="small"
            data-testid="player-row-menu"
            :aria-label="`Actions for ${player.name}`"
            @click="openPlayerMenu($event, player)"
          />
        </div>
      </div>

      <!-- One empty state, only when there are no relationships at all -->
      <div
        v-if="contacts.length === 0 && players.length === 0"
        class="text-center py-8 text-gray-500"
      >
        <i class="pi pi-users text-3xl mb-2 block" />
        <p>No relationships yet</p>
        <p class="text-xs mt-1">
          Parents can view and manage players; payers handle billing.
        </p>
      </div>
    </template>

    <Menu ref="rowMenu" :model="rowMenuItems" popup append-to="body" />
    <Menu ref="addMenu" :model="addMenuItems" popup append-to="body" />
    <ConfirmDialog group="removeRelationship" />

    <AddRelationshipDrawer
      v-if="showAddDrawer"
      v-model:visible="showAddDrawer"
      :direction="addDirection"
      :user="user"
      :organization-id="organizationId"
      @relationship-added="handleRelationshipChanged"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import Button from "primevue/button";
import Tag from "primevue/tag";
import Menu from "primevue/menu";
import ConfirmDialog from "primevue/confirmdialog";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import UserAvatar from "@/shared/components/UserAvatar.vue";
import AddRelationshipDrawer from "./AddRelationshipDrawer.vue";
import { getUserInitials, getRoleTagProps } from "../helpers";
import { ProfileRole } from "../constants/roles";
import {
  getAssociatedContacts,
  removeContactFromUser,
  updateAssociatedContactApi,
  linkPlayerToContact,
} from "../api";
import { ROUTE_NAME } from "@/shared/constants/routes";
import type {
  AdminProfile,
  AdminProfileAssociatedContact,
  LinkedPlayerProfile,
} from "@/api/model";

interface Props {
  readonly user: AdminProfile;
  readonly organizationId: number;
}

const props = defineProps<Props>();
const emit = defineEmits<{ "refresh-user": [] }>();

const router = useRouter();
const confirm = useConfirm();
const toast = useToast();

const isPlayer = computed(() => props.user.roles?.includes(ProfileRole.player));
const isContact = computed(() =>
  props.user.roles?.includes(ProfileRole.contact)
);

// Player side: this player's parents/payers
const contacts = ref<AdminProfileAssociatedContact[]>([]);
const isLoadingContacts = ref(false);

async function loadContacts() {
  if (!isPlayer.value) return;
  isLoadingContacts.value = true;
  try {
    const response = await getAssociatedContacts(props.user.id);
    contacts.value = "error" in response ? [] : response.results || [];
  } catch {
    contacts.value = [];
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to load relationships. Please refresh.",
      life: 5000,
    });
  } finally {
    isLoadingContacts.value = false;
  }
}

onMounted(loadContacts);

// Contact side: players this person manages/pays for
const players = computed(() => props.user.linked_player_profiles || []);
const contactId = computed<number | null>(
  () => props.user.associated_contact_id ?? null
);

// Add Relationship drawer
const showAddDrawer = ref(false);
const addDirection = ref<"add-contact" | "add-player">("add-contact");

function openAddDrawer(direction: "add-contact" | "add-player") {
  addDirection.value = direction;
  showAddDrawer.value = true;
}

// One button: single-role users go straight to the drawer; dual-role users
// pick which side of the relationship they're adding.
const addMenu = ref();
const addMenuItems = computed(() => [
  {
    label: `Parent or payer for ${props.user.name}`,
    icon: "pi pi-users",
    command: () => openAddDrawer("add-contact"),
  },
  {
    label: "A player they manage",
    icon: "pi pi-user",
    command: () => openAddDrawer("add-player"),
  },
]);

function handleAddClick(event: Event) {
  if (isPlayer.value && isContact.value) {
    addMenu.value.toggle(event);
    return;
  }
  // Players add contacts; contacts and roleless users add players (MAI-4998).
  openAddDrawer(isPlayer.value ? "add-contact" : "add-player");
}

// The button lives on the tab bar row (UserDetailView), per the design.
defineExpose({ handleAddClick });

function handleRelationshipChanged() {
  loadContacts();
  emit("refresh-user");
}

// Row kebab menu
const rowMenu = ref();
const rowMenuItems = ref<any[]>([]);

function openContactMenu(event: Event, contact: AdminProfileAssociatedContact) {
  rowMenuItems.value = [
    ...(contact.associated_contacts_profile_id
      ? [
          {
            label: "View profile",
            icon: "pi pi-user",
            command: () =>
              router.push({
                name: ROUTE_NAME.USER_DETAIL,
                params: { id: contact.associated_contacts_profile_id },
              }),
          },
        ]
      : []),
    contact.primary_contact
      ? {
          label: "Remove default payer",
          icon: "pi pi-credit-card",
          command: () => setDefaultPayer(contact, false),
        }
      : {
          label: "Make default payer",
          icon: "pi pi-credit-card",
          command: () => setDefaultPayer(contact, true),
        },
    contact.is_parent !== false
      ? {
          label: "Make payer only",
          icon: "pi pi-users",
          command: () => setRelationshipType(contact.id!, props.user.id, false),
        }
      : {
          label: "Make parent",
          icon: "pi pi-users",
          command: () => setRelationshipType(contact.id!, props.user.id, true),
        },
    {
      label: "Remove relationship",
      icon: "pi pi-times",
      command: () =>
        confirmRemove(contact.name || "this person", () =>
          removeContactFromUser(props.user.id, contact.id!)
        ),
    },
  ];
  rowMenu.value.toggle(event);
}

function openPlayerMenu(event: Event, player: LinkedPlayerProfile) {
  rowMenuItems.value = [
    {
      label: "View profile",
      icon: "pi pi-user",
      command: () =>
        router.push({
          name: ROUTE_NAME.USER_DETAIL,
          params: { id: player.id },
        }),
    },
    // Without a contact record there is no link to retype or remove.
    ...(contactId.value != null
      ? [
          player.is_parent !== false
            ? {
                label: "Make payer only",
                icon: "pi pi-users",
                command: () =>
                  setRelationshipType(contactId.value!, player.id, false),
              }
            : {
                label: "Make parent",
                icon: "pi pi-users",
                command: () =>
                  setRelationshipType(contactId.value!, player.id, true),
              },
          {
            label: "Remove relationship",
            icon: "pi pi-times",
            command: () =>
              confirmRemove(player.name || "this player", () =>
                removeContactFromUser(player.id, contactId.value!)
              ),
          },
        ]
      : []),
  ];
  rowMenu.value.toggle(event);
}

// Flip a link's parent/payer type (MAI-5007): add_profile updates is_parent
// on the existing AssociatedContactProfile row.
async function setRelationshipType(
  acId: number,
  playerProfileId: number,
  isParent: boolean
) {
  const result = await linkPlayerToContact(
    props.organizationId,
    acId,
    playerProfileId,
    { is_parent: isParent }
  );
  if (result && "error" in result) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: String(result.error) || "Failed to update the relationship.",
      life: 5000,
    });
    return;
  }
  toast.add({
    severity: "success",
    summary: "Relationship updated",
    detail: isParent ? "Now a parent or guardian." : "Now a payer only.",
    life: 3000,
  });
  handleRelationshipChanged();
}

async function setDefaultPayer(
  contact: AdminProfileAssociatedContact,
  isDefault: boolean
) {
  const result = await updateAssociatedContactApi(props.user.id, contact.id!, {
    primary_contact: isDefault,
  });
  if (result && "error" in result) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to update default payer",
      life: 5000,
    });
    return;
  }
  handleRelationshipChanged();
}

function confirmRemove(name: string, remove: () => Promise<unknown>) {
  confirm.require({
    group: "removeRelationship",
    message: `Remove the relationship with ${name}?`,
    header: "Remove Relationship",
    icon: "pi pi-exclamation-triangle",
    rejectLabel: "Cancel",
    acceptLabel: "Remove",
    acceptClass: "p-button-danger",
    accept: async () => {
      const result: any = await remove();
      if (result && typeof result === "object" && "error" in result) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Failed to remove relationship",
          life: 5000,
        });
        return;
      }
      handleRelationshipChanged();
    },
  });
}
</script>
